import { InvoiceKindChip } from '../../components/InvoiceKind';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { displayCents, useMoneyMode } from '../../lib/money';
import type { FinanceReport, InvoiceStatus, InvoiceWithNames } from '../../../shared/types';
import { BulkActionBar, formatInvoiceNumber, formatMoney, Pill, Vital, Vitals } from '../../components/primitives';
import { useBulkSelect } from '../../lib/useBulkSelect';
import { useListKeyboard } from '../../lib/listKeyboard';
import { reportError } from '../../lib/errorEvents';
import { DotsIcon } from '../../components/icons';
import { SwipeRow } from '../../components/SwipeRow';
import { InvoiceRowSheet, logInvoiceReminder } from '../../components/InvoiceRowSheet';
import { invoiceDateMeta, invoiceGroupValue, invoiceVitalsLine } from '../../lib/financeListMeta';
import type { FinanceSelection } from './types';
import { PAID_WINDOW, effectiveInvoiceBrand, displayStatus, matchesQuery } from './helpers';

// ── Invoices tab ──────────────────────────────────────────────────────────

export function InvoicesTab({
  invoices,
  report,
  query,
  clientFilter,
  today,
  currentQuarterKey,
  fyKey,
  showAllPaid,
  onShowAllPaid,
  onOpen,
  onChanged,
  modalOpen,
  isMobile,
  selectMode,
  onSelectMode,
  onSelection,
}: {
  invoices: InvoiceWithNames[];
  report: FinanceReport | null;
  query: string;
  clientFilter: string | null;
  today: string;
  currentQuarterKey: string;
  fyKey: string;
  showAllPaid: boolean;
  onShowAllPaid: () => void;
  onOpen: (id: string) => void;
  /** Refetch after a bulk action (Phase 1.13). */
  onChanged: () => void;
  /** True while the new-invoice modal is open — disables the keyboard layer. */
  modalOpen: boolean;
  /** ≤880px (§8.1 A): two vitals cards, a mono line and swipeable rows. */
  isMobile: boolean;
  /** Bulk select, entered from the page menu, exited from the header. */
  selectMode: boolean;
  onSelectMode: (on: boolean) => void;
  onSelection: (selection: FinanceSelection | null) => void;
}) {
  const mode = useMoneyMode();
  /** The row whose "···" sheet is open (≤880px). */
  const [rowSheet, setRowSheet] = useState<InvoiceWithNames | null>(null);
  const disp = (inv: InvoiceWithNames) => displayCents(inv.amount_cents, inv.gst_cents, mode);
  const filtered = useMemo(
    () =>
      invoices.filter(
        (inv) =>
          (!clientFilter || inv.client_id === clientFilter) &&
          matchesQuery(query, inv.invoice_number, inv.client_name, inv.project_name, inv.notes),
      ),
    [invoices, clientFilter, query],
  );

  // Unpaid invoices on a voided project are money that isn't being chased —
  // the vitals already exclude them, so they sit in their own muted group at
  // the bottom instead of inflating Overdue/Sent/Draft. Paid ones stay put:
  // they're banked revenue regardless of what happened to the project.
  const onVoidedProject = (inv: InvoiceWithNames) =>
    inv.project_voided_at !== null && displayStatus(inv, today) !== 'paid';
  const live = filtered.filter((inv) => !onVoidedProject(inv));
  const voidedProject = filtered.filter(onVoidedProject);

  const byStatus = (s: InvoiceStatus) => live.filter((inv) => displayStatus(inv, today) === s);
  const overdue = byStatus('overdue');
  const sent = byStatus('sent');
  const draft = byStatus('draft');
  // "Paid (recent)" lists by payment recency, not the API's issue order.
  const paid = byStatus('paid').sort((a, b) =>
    (b.paid_date ?? b.issue_date ?? '').localeCompare(a.paid_date ?? a.issue_date ?? ''),
  );
  const voided = byStatus('void');

  // Bulk-select + keyboard (Phase 1.13) — ids in the same on-screen order as
  // the groups render below, so j/k walk exactly what's visible.
  const visibleIds = useMemo(() => {
    const paidVisible = showAllPaid ? paid : paid.slice(0, PAID_WINDOW);
    return [...overdue, ...sent, ...draft, ...paidVisible, ...voided, ...voidedProject].map((i) => i.id);
  }, [overdue, sent, draft, paid, voided, voidedProject, showAllPaid]);
  const bulk = useBulkSelect(visibleIds);
  const kb = useListKeyboard({
    ids: visibleIds,
    enabled: !modalOpen,
    onToggle: bulk.toggle,
    onOpen,
    onClear: bulk.clear,
  });
  const runBulkTrash = async () => {
    const ids = bulk.ids;
    if (ids.length === 0) return;
    try {
      await api('/api/admin/finance/invoices/bulk', { method: 'POST', json: { ids, action: 'trash' } });
    } catch (error) {
      reportError(error, 'Could not move the selected invoices to trash.');
      return;
    }
    bulk.clear();
    onChanged();
  };

  // ── Mobile select mode: the header owns the chrome, the tab owns the state.
  const selectAll = () => visibleIds.forEach((id) => { if (!bulk.isSelected(id)) bulk.toggle(id); });
  const exitSelect = () => { bulk.clear(); onSelectMode(false); };
  // The published handlers read through a ref so the effect only fires on a
  // real count/mode change, not on every closure identity.
  const selectRef = useRef({ selectAll, exitSelect });
  selectRef.current = { selectAll, exitSelect };
  useEffect(() => {
    onSelection(
      isMobile && selectMode
        ? {
            count: bulk.count,
            onSelectAll: () => selectRef.current.selectAll(),
            onDone: () => selectRef.current.exitSelect(),
          }
        : null,
    );
  }, [isMobile, selectMode, bulk.count, onSelection]);
  useEffect(() => () => onSelection(null), [onSelection]);

  const sum = (list: InvoiceWithNames[]) => list.reduce((s, i) => s + disp(i), 0);
  const paidQuarter = report?.quarters.find((q) => q.key === currentQuarterKey);
  const paidQuarterCents = mode === 'ex' ? (paidQuarter?.paid_ex_cents ?? 0) : (paidQuarter?.paid_cents ?? 0);
  const invoicedFY = (report?.quarters ?? [])
    .filter((q) => q.key.startsWith(fyKey))
    .reduce((s, q) => s + (mode === 'ex' ? q.invoiced_ex_cents : q.invoiced_cents), 0);

  const dateMeta = (inv: InvoiceWithNames) => invoiceDateMeta(inv, displayStatus(inv, today), today);

  const row = (inv: InvoiceWithNames) => {
    const meta = dateMeta(inv);
    return (
      <div
        className={`row row-bulk-gutter${bulk.count > 0 ? ' selactive' : ''}${kb.focusedId === inv.id ? ' kbd-focus' : ''}`}
        key={inv.id}
        onClick={() => onOpen(inv.id)}
      >
        <span
          className={`bulk-check${bulk.isSelected(inv.id) ? ' on' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            bulk.toggle(inv.id);
          }}
        />
        <span className="inv-num">{formatInvoiceNumber(inv.invoice_number)}</span>
        <InvoiceKindChip kind={inv.invoice_kind} />
        <Pill tone={effectiveInvoiceBrand(inv) === 'hsp' ? 'accent' : undefined}>
          {effectiveInvoiceBrand(inv)}
        </Pill>
        <span className="row-title" title={inv.client_name}>{inv.client_name}</span>
        {inv.project_name && <span className="row-context" title={inv.project_name}>{inv.project_name}</span>}
        <span className="row-spacer" />
        <span className={`row-meta col-date ${meta.tone}`}>{meta.text}</span>
        <span className="row-meta gold col-amt">{formatMoney(disp(inv))}</span>
      </div>
    );
  };

  /** ≤880px row (§8.1 A): number + brand, client · project, the date-meta in
      its tone, amount gold. Swipe is for sent/overdue only; the "···" beside
      it reaches the same actions, so nothing is gesture-only. */
  const mobileRow = (inv: InvoiceWithNames) => {
    const status = displayStatus(inv, today);
    const meta = invoiceDateMeta(inv, status, today);
    const chaseable = status === 'sent' || status === 'overdue';
    const selected = bulk.isSelected(inv.id);
    const label = formatInvoiceNumber(inv.invoice_number);
    return (
      <SwipeRow
        key={inv.id}
        className="finance-swipe"
        disabled={selectMode || !chaseable}
        leftActions={
          chaseable
            ? [
                {
                  label: 'Reminder',
                  icon: '✉',
                  tone: 'gold' as const,
                  // A failed reminder must not refetch as if it worked (review C-7).
                  onSelect: () =>
                    void logInvoiceReminder(inv.id).then(onChanged, (error) =>
                      reportError(error, 'Could not log the reminder.'),
                    ),
                },
                { label: 'More', icon: '···', tone: 'grey' as const, onSelect: () => setRowSheet(inv) },
              ]
            : undefined
        }
      >
        <div className={`finance-row${selected ? ' sel' : ''}`}>
          {selectMode && (
            <button
              type="button"
              className="finance-select-hit"
              role="checkbox"
              aria-checked={selected}
              aria-label={`Select ${label}`}
              onClick={() => bulk.toggle(inv.id)}
            >
              <span className={`check finance-select-check${selected ? ' done' : ''}`} />
            </button>
          )}
          <button
            type="button"
            className="finance-row-open"
            aria-label={`Open ${label}`}
            onClick={() => (selectMode ? bulk.toggle(inv.id) : onOpen(inv.id))}
          >
            <span className="finance-row-col">
              <span className="finance-row-title">
                {label}
                <InvoiceKindChip kind={inv.invoice_kind} />
                <Pill tone={effectiveInvoiceBrand(inv) === 'hsp' ? 'accent' : undefined}>
                  {effectiveInvoiceBrand(inv)}
                </Pill>
              </span>
              <span className="finance-row-meta">
                <span className="who">{inv.client_name}</span>
                {inv.project_name && (
                  <>
                    <span className="sep">·</span>
                    <span className="what">{inv.project_name}</span>
                  </>
                )}
              </span>
              <span className={`finance-row-date${meta.tone === 'danger' ? ' danger' : ''}`}>{meta.text}</span>
            </span>
            <span className="finance-row-right">
              <span className="finance-row-money">{formatMoney(disp(inv))}</span>
            </span>
          </button>
          {!selectMode && (
            <button
              type="button"
              className="finance-row-more"
              aria-label={`Actions for ${label}`}
              onClick={() => setRowSheet(inv)}
            >
              <DotsIcon />
            </button>
          )}
        </div>
      </SwipeRow>
    );
  };

  const group = (
    title: string,
    rows: InvoiceWithNames[],
    dot: ReactNode,
    opts?: {
      titleStyle?: CSSProperties;
      count?: ReactNode;
      value?: ReactNode;
      children?: ReactNode;
      muted?: boolean;
    },
  ) =>
    rows.length > 0 || opts?.children ? (
      <div className={`status-group${opts?.muted ? ' muted' : ''}`} key={title}>
        <div className="group-head">
          {dot}
          <span className="group-title" style={opts?.titleStyle}>
            {title}
          </span>
          <span className="group-count">{opts?.count ?? rows.length}</span>
          <span className="group-value">{opts?.value ?? formatMoney(sum(rows))}</span>
        </div>
        {rows.map(row)}
        {opts?.children}
      </div>
    ) : null;

  // ── ≤880px (§8.1 A) ────────────────────────────────────────────────────
  if (isMobile) {
    const line = invoiceVitalsLine({
      quarterKey: currentQuarterKey,
      paidQuarterCents,
      invoicedFYCents: invoicedFY,
      exGst: mode === 'ex',
    });
    const paidVisible = showAllPaid ? paid : paid.slice(0, PAID_WINDOW);
    // Overdue / Sent / Draft / Paid always show their header — an empty one
    // is the answer to "is anything overdue?". Void and Voided projects are
    // exception shelves, so they appear only when they hold something.
    const mgroup = (
      title: string,
      rows: InvoiceWithNames[],
      dot: ReactNode,
      opts?: { danger?: boolean; count?: ReactNode; value?: ReactNode; muted?: boolean; children?: ReactNode },
    ) => (
      <section className={`finance-group-sec${opts?.muted ? ' muted' : ''}`} key={title}>
        <div className="finance-group">
          {dot}
          <span className={opts?.danger ? 'danger' : undefined}>{title}</span>
          <span className="finance-group-count">{opts?.count ?? rows.length}</span>
          <span className="finance-group-value">
            {opts?.value ?? (rows.length > 0 ? invoiceGroupValue(rows.map(disp)) : '')}
          </span>
        </div>
        {rows.map(mobileRow)}
        {opts?.children}
      </section>
    );
    return (
      <>
        <Vitals>
          <Vital
            label="Outstanding"
            value={formatMoney(mode === 'ex' ? (report?.outstanding_ex_cents ?? 0) : (report?.outstanding_cents ?? 0))}
            tone="gold"
            sub={`${report?.outstanding_count ?? 0} invoices`}
          />
          <Vital
            label="Overdue"
            value={formatMoney(mode === 'ex' ? (report?.overdue_ex_cents ?? 0) : (report?.overdue_cents ?? 0))}
            tone={report?.overdue_count ? 'danger' : undefined}
            sub={`${report?.overdue_count ?? 0} invoices`}
          />
        </Vitals>
        <div className="finance-line">
          <span className="accent">{line.left}</span>
          <span>{line.right}</span>
        </div>

        {mgroup('Overdue', overdue, <span className="phase-dot" style={{ background: 'var(--danger)' }} />, { danger: true })}
        {mgroup('Sent', sent, <span className="phase-dot" style={{ background: 'var(--gold)' }} />)}
        {mgroup('Draft', draft, <span className="phase-dot pd-lead" />)}
        {mgroup('Paid', paidVisible, <span className="phase-dot" style={{ background: 'var(--ok)' }} />, {
          count: 'recent',
          value: `${formatMoney(paidQuarterCents)} this quarter`,
          children:
            !showAllPaid && paid.length > PAID_WINDOW ? (
              <button className="show-more" onClick={onShowAllPaid}>
                Show all {filtered.length} invoices →
              </button>
            ) : undefined,
        })}
        {voided.length > 0 && mgroup('Void', voided, <span className="phase-dot" style={{ background: 'var(--text-faint)' }} />)}
        {voidedProject.length > 0 &&
          mgroup('Voided projects', voidedProject, <span className="phase-dot" style={{ background: 'var(--text-faint)' }} />, { muted: true })}
        {filtered.length === 0 && <div className="finance-empty">No invoices match.</div>}

        {rowSheet && (
          <InvoiceRowSheet
            invoice={{
              id: rowSheet.id,
              number: formatInvoiceNumber(rowSheet.invoice_number),
              context: [rowSheet.client_name, rowSheet.project_name].filter(Boolean).join(' · '),
              status: displayStatus(rowSheet, today),
            }}
            onOpen={() => {
              setRowSheet(null);
              onOpen(rowSheet.id);
            }}
            onClose={() => setRowSheet(null)}
            onChanged={onChanged}
          />
        )}
        <BulkActionBar
          count={bulk.count}
          actions={[{ key: 'trash', label: 'Trash', danger: true, onClick: runBulkTrash }]}
          onClear={exitSelect}
        />
      </>
    );
  }

  return (
    <>
      <Vitals>
        <Vital
          label="Outstanding"
          value={formatMoney(mode === 'ex' ? (report?.outstanding_ex_cents ?? 0) : (report?.outstanding_cents ?? 0))}
          tone="gold"
          sub={`${report?.outstanding_count ?? 0} invoices`}
        />
        <Vital
          label="Overdue"
          value={formatMoney(mode === 'ex' ? (report?.overdue_ex_cents ?? 0) : (report?.overdue_cents ?? 0))}
          tone={report?.overdue_count ? 'danger' : undefined}
          sub={`${report?.overdue_count ?? 0} invoices`}
        />
        <Vital
          label="Paid this quarter"
          value={formatMoney(paidQuarterCents)}
          tone="accent"
          sub={currentQuarterKey}
        />
        <Vital label="Invoiced this FY" value={formatMoney(invoicedFY)} sub={mode === 'ex' ? 'ex GST' : 'incl. GST'} />
      </Vitals>

      {group('Overdue', overdue, <span className="phase-dot" style={{ background: 'var(--danger)' }} />, {
        titleStyle: { color: 'var(--danger)' },
      })}
      {group('Sent', sent, <span className="phase-dot" style={{ background: 'var(--gold)' }} />)}
      {group('Draft', draft, <span className="phase-dot pd-lead" />)}
      {group('Paid', showAllPaid ? paid : paid.slice(0, PAID_WINDOW), <span className="phase-dot" style={{ background: 'var(--ok)' }} />, {
        count: 'recent',
        value: `${formatMoney(paidQuarterCents)} this quarter`,
        children:
          !showAllPaid && paid.length > PAID_WINDOW ? (
            <button className="show-more" onClick={onShowAllPaid}>
              Show all {filtered.length} invoices →
            </button>
          ) : undefined,
      })}
      {group('Void', voided, <span className="phase-dot" style={{ background: 'var(--text-faint)' }} />)}
      {group(
        'Voided projects',
        voidedProject,
        <span className="phase-dot" style={{ background: 'var(--text-faint)' }} />,
        { titleStyle: { color: 'var(--text-faint)' }, muted: true },
      )}
      {filtered.length === 0 && <div className="empty-note">No invoices match.</div>}
      <BulkActionBar
        count={bulk.count}
        actions={[{ key: 'trash', label: 'Trash', danger: true, onClick: runBulkTrash }]}
        onClear={bulk.clear}
      />
    </>
  );
}
